import { useEffect, useState } from "react";
import { RANGE_LABEL, STEP_LABEL, type RangeKey, type ScopeResponse } from "@k8s-dashboard/contracts";
import { REFRESH_OPTIONS } from "@/state/useDashboardParams";
import { clock } from "@/lib/format";
import { Combobox } from "./Combobox";

/* ── Namespace Selector ──────────────────────────────────────────────────── */

/**
 * Header의 전역 namespace 선택 (#94). Observer는 단일 클러스터 배포라 클러스터는 고르지 않습니다.
 *
 * 옵션은 검색형 콤보박스로 노출합니다 — namespace가 수백 개여도 스크롤·검색으로
 * 찾을 수 있습니다. 전체(all) scope의 이름 목록은 서버가 /scope 응답의
 * availableNamespaces로 열거합니다. (#1)
 *
 * 여기서 고른 값은 힌트일 뿐이고, 실제 Scope는 서버가 토큰에서 강제합니다. (README §10)
 */
export function NamespaceSelector({
  scope,
  clusterId,
  namespace,
  onChange,
  disabled,
  label,
  allLabel,
}: {
  scope: ScopeResponse | undefined;
  clusterId: string;
  namespace: string;
  onChange: (namespace: string) => void;
  disabled?: boolean;
  /** 보이는 라벨이자 접근성 이름. */
  label: string;
  /** "고르지 않음(all)" 옵션의 표시 이름. */
  allLabel: string;
}) {
  const current = scope?.clusters.find((c) => c.id === clusterId);
  const nsOptions =
    current?.namespaces === "all" ? (current.availableNamespaces ?? []) : (current?.namespaces ?? []);
  /* 링크가 가리킨 namespace가 Scope 목록에 없을 수 있습니다(권한 밖·삭제됨). 그래도 지금 무엇으로
     조회 중인지는 보여야 하므로 현재 값을 옵션에 넣습니다 — 빈칸이면 "아무것도 안 고름"으로 읽힙니다.
     권한 판정은 서버가 하고 화면이 사유를 보여줍니다. (#103 후속) */
  const options = namespace !== "all" && !nsOptions.includes(namespace) ? [namespace, ...nsOptions] : nsOptions;

  return (
    <div className="scope">
      <span className="scope__label" aria-hidden="true">
        {label} :
      </span>
      <Combobox
        id="scope-ns"
        label={label}
        value={namespace}
        disabled={disabled || !current}
        options={[{ value: "all", label: allLabel }, ...options.map((ns) => ({ value: ns, label: ns }))]}
        onSelect={onChange}
      />
    </div>
  );
}

/* ── Time Range ──────────────────────────────────────────────────────────── */

const PRESETS: RangeKey[] = ["30d", "7d", "1d", "1h"];
/** Custom Range 상한 — 서버(timerange.MaxCustomSpan)와 같은 30일입니다. 서버가 정본이고 여기 검증은 안내용입니다. */
const CUSTOM_MAX_MS = 30 * 24 * 60 * 60 * 1000;

/** epoch ms(문자열) → datetime-local 입력값(로컬 시각 `YYYY-MM-DDTHH:mm`). */
function msToLocalInput(ms?: string): string {
  if (!ms) return "";
  const n = Number(ms);
  if (!Number.isFinite(n)) return "";
  const d = new Date(n);
  const pad = (x: number) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** datetime-local 입력값 → epoch ms. 비었거나 형식이 틀리면 null. */
function localInputToMs(v: string): number | null {
  if (!v) return null;
  const n = new Date(v).getTime();
  return Number.isFinite(n) ? n : null;
}

/**
 * 시간 범위 컨트롤. Custom은 **누르는 즉시 조회하지 않습니다** — 날짜 편집창만 열고,
 * from/to가 유효할 때 `onCustom`으로 한 번에 커밋합니다. 그 전까지 조회는 직전 프리셋을
 * 유지하므로 `range=custom`이 from/to 없이 서버로 나가는 일이 없습니다. (#58)
 */
export function TimeRangePicker({
  range,
  from,
  to,
  onChange,
  onCustom,
}: {
  range: RangeKey;
  /** custom이 적용 중일 때의 시작·종료(epoch ms 문자열). */
  from?: string;
  to?: string;
  /** 프리셋 선택. 부모가 from/to를 함께 지웁니다. */
  onChange: (r: RangeKey) => void;
  /** custom 적용. 부모가 range=custom과 from/to를 함께 URL에 씁니다. */
  onCustom: (from: string, to: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [fromDraft, setFromDraft] = useState(() => msToLocalInput(from));
  const [toDraft, setToDraft] = useState(() => msToLocalInput(to));
  const [error, setError] = useState<string | null>(null);
  const stepLabel = range === "custom" ? "15분" : STEP_LABEL[range];

  /* 딥링크·되돌아오기로 URL의 from/to가 바뀌면 편집창 초안을 맞춥니다. */
  useEffect(() => {
    setFromDraft(msToLocalInput(from));
    setToDraft(msToLocalInput(to));
  }, [from, to]);

  const openCustom = () => {
    /* 프리셋 상태·조회를 건드리지 않고 편집창만 엽니다. 초안이 비었으면 최근 1시간으로 채웁니다. */
    if (!fromDraft && !toDraft) {
      const now = Date.now();
      setToDraft(msToLocalInput(String(now)));
      setFromDraft(msToLocalInput(String(now - 60 * 60 * 1000)));
    }
    setError(null);
    setOpen((v) => !v);
  };

  const apply = () => {
    const f = localInputToMs(fromDraft);
    const t = localInputToMs(toDraft);
    if (f === null || t === null) {
      setError("시작과 종료를 모두 선택하세요.");
      return;
    }
    if (t <= f) {
      setError("종료가 시작보다 뒤여야 합니다.");
      return;
    }
    if (t - f > CUSTOM_MAX_MS) {
      const days = Math.ceil((t - f) / (24 * 60 * 60 * 1000));
      setError(`최대 조회 범위는 30일입니다. 현재 ${days}일 선택됨`);
      return;
    }
    setError(null);
    setOpen(false);
    onCustom(String(f), String(t));
  };

  const pickPreset = (r: RangeKey) => {
    setOpen(false);
    setError(null);
    onChange(r);
  };

  return (
    <div className="ds-time-range" style={{ position: "relative" }}>
      <div className="ds-time-range__presets" role="group" aria-label="시간 범위">
        {PRESETS.map((r) => (
          <button
            key={r}
            type="button"
            className="ds-time-range__preset"
            aria-pressed={range === r}
            onClick={() => pickPreset(r)}
          >
            {RANGE_LABEL[r].replace("최근 ", "")}
          </button>
        ))}
        <button
          type="button"
          className="ds-time-range__preset"
          aria-pressed={range === "custom"}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={openCustom}
        >
          Custom
        </button>
      </div>
      <span className="ds-time-range__state">
        <span className="ds-time-range__step">step {stepLabel}</span>
      </span>
      <div
        className="ds-time-range__popover"
        role="dialog"
        aria-label="사용자 지정 기간"
        hidden={!open}
        style={{ top: "calc(var(--control-height-sm) + var(--space-2))", left: 0 }}
      >
        <div className="ds-time-range__field">
          <label htmlFor="tr-from">시작</label>
          <input id="tr-from" type="datetime-local" value={fromDraft} onChange={(e) => setFromDraft(e.target.value)} />
        </div>
        <div className="ds-time-range__field">
          <label htmlFor="tr-to">종료</label>
          <input id="tr-to" type="datetime-local" value={toDraft} onChange={(e) => setToDraft(e.target.value)} />
        </div>
        <span className="ds-time-range__error" hidden={!error}>
          <span aria-hidden="true">✕</span> {error}
        </span>
        <span className="ds-time-range__hint">서버가 범위·Step·Data Point·Result Size를 강제합니다.</span>
        <div className="row">
          <button type="button" className="linkish" onClick={() => setOpen(false)}>
            취소
          </button>
          <button type="button" onClick={apply}>
            적용
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Refresh ─────────────────────────────────────────────────────────────── */

/**
 * 자동 갱신은 **데이터만** 갱신합니다. 페이지를 다시 그리지 않습니다.
 * TanStack Query의 `placeholderData: keepPreviousData` 덕분에 값 교체 중에도
 * 레이아웃과 스크롤 위치가 유지됩니다. (이슈 #14 완료 기준)
 */
export function RefreshControl({
  refreshMs,
  onChange,
  onRefreshNow,
  fetching,
  updatedAtMs,
}: {
  refreshMs: number;
  onChange: (ms: number) => void;
  onRefreshNow: () => void;
  fetching: boolean;
  /** 마지막으로 응답을 받은 실제 시각. mock의 관측 시각과 구분합니다. */
  updatedAtMs?: number;
}) {
  return (
    <div className="refresh">
      <label className="visually-hidden" htmlFor="refresh-interval">
        자동 갱신 주기
      </label>
      <select id="refresh-interval" value={refreshMs} onChange={(e) => onChange(Number(e.target.value))}>
        {REFRESH_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            자동 갱신: {o.label}
          </option>
        ))}
      </select>
      <button type="button" className="linkish" onClick={onRefreshNow} aria-live="off">
        지금 갱신
      </button>
      {/* 텍스트가 "갱신 중…"↔"마지막 갱신 HH:MM"로 바뀌어도 행 폭이 흔들리지 않게
          최장 문구만큼 폭을 예약합니다. (#17) */}
      <span className="refresh__status" aria-live="polite">
        {fetching ? "갱신 중…" : updatedAtMs ? `마지막 갱신 ${clock(updatedAtMs)}` : ""}
      </span>
    </div>
  );
}
